import { Box, Button, Flex, Heading, Spinner } from '@chakra-ui/react';
import Carousel from 'nuka-carousel';
import { ChevronLeft } from 'lucide-react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { InternationalEventResponse } from 'api/responseTypes';
import { InternationalEventListItem } from 'components/InternationalEventListItem/InternationalEventListItem';
import { useEventTracking } from 'hooks/useEventTracking';
import { useInternationalEventsQuery } from 'pages/InternationalEvents/hooks/useInternationalEvents';
import { ROUTES } from 'router/routes';
import { resolveInternationalEventsEnabled } from 'utils/internationalEvents';

/** How many events to preview on the home feed before "عرض الكل". */
const HOME_PREVIEW_COUNT = 6;

/** Rows shown at once; extra events page in via the carousel. */
const ROWS_PER_SLIDE = 3;

/** Compact single-column width so rows read like the design (thumbnail beside text). */
const LIST_MAX_W = { base: '100%', md: '720px' };

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const groups: T[][] = [];
  for (let i = 0; i < arr.length; i += size) groups.push(arr.slice(i, i + size));
  return groups;
};

/**
 * Additive home-feed section for international events: a header with a "عرض الكل"
 * (view all) link and a compact carousel showing three events per slide. Rendered
 * below local content and only when the feature is enabled for the current platform.
 */
export const InternationalEventsSection = () => {
  const navigate = useNavigate();
  const { eventTracking } = useEventTracking();
  const { isLoading, items, mode, isCompactList } = useInternationalEventsQuery();

  const [currentSlide, setCurrentSlide] = useState(0);
  const goToSlideRef = useRef<((index: number) => void) | null>(null);

  const isEnabled = resolveInternationalEventsEnabled(mode);

  // This is the compact-list surface: only render it when the backend asks for the
  // compact list. In card mode international events render as cards inside the feed.
  // Also bail when the feature is off or there's nothing to show once loaded.
  if (!isEnabled || !isCompactList || (!isLoading && items.length === 0)) {
    return null;
  }

  const previewItems = items.slice(0, HOME_PREVIEW_COUNT);
  const slides = chunk(previewItems, ROWS_PER_SLIDE);

  const handleViewAll = () => {
    navigate(`/${ROUTES.internationalEvents}`);
  };

  const handleItemClick = (event: InternationalEventResponse) => {
    eventTracking.internationalEvent.clicked({
      eventId: event.id,
      eventName: event.title,
      eventDate: event.date ?? null,
      venue: event.city ?? undefined,
      source: 'home_page',
    });
    navigate('/international-event/' + event.id);
  };

  const renderGroup = (group: InternationalEventResponse[]) => (
    <Box as='ul' dir='rtl' listStyleType='none' p={0} display='flex' flexDirection='column' gap={5}>
      {group.map((event) => (
        <InternationalEventListItem
          key={event.id}
          event={event}
          onCardClick={() => handleItemClick(event)}
        />
      ))}
    </Box>
  );

  return (
    <Box
      as='section'
      aria-labelledby='headingInternationalEvents'
      width='100vw'
      bg='#E8E5E2'
      // Mobile spec: 20px inline / 32px block padding; scales up on larger screens.
      px={{ base: 5, md: 8, lg: 10 }}
      py={{ base: 8, md: 10 }}
      dir='rtl'
    >
      <Flex
        maxW={LIST_MAX_W}
        mx='auto'
        align='center'
        justify='space-between'
        mb={{ base: 4, md: 6 }}
      >
        <Heading
          as='h2'
          id='headingInternationalEvents'
          variant={{ base: 'headingSmall', md: 'headingMedium' }}
        >
          <FormattedMessage
            defaultMessage='الفعاليات الدولية'
            id='homepage.international.heading'
          />
        </Heading>

        <Button
          variant='link'
          color='green.G500'
          fontWeight='600'
          rightIcon={<ChevronLeft size={18} />}
          onClick={handleViewAll}
        >
          <FormattedMessage defaultMessage='عرض الكل' id='homepage.viewAll' />
        </Button>
      </Flex>

      {isLoading ? (
        <Flex align='center' justify='center' py={12}>
          <Spinner color='green.G50' size='xl' thickness='4px' />
        </Flex>
      ) : slides.length <= 1 ? (
        <Box maxW={LIST_MAX_W} mx='auto'>
          {renderGroup(previewItems)}
        </Box>
      ) : (
        // nuka needs an LTR track; cellAlign='right' + RTL content gives the RTL feel.
        <Box maxW={LIST_MAX_W} mx='auto' dir='ltr'>
          <Carousel
            afterSlide={setCurrentSlide}
            wrapAround
            // RTL: current slide hugs the right, the 20% peek of the next slide shows on the left.
            cellAlign='right'
            slidesToShow={1.2}
            slidesToScroll={1}
            cellSpacing={12}
            dragging
            swiping
            adaptiveHeight
            renderCenterLeftControls={(props) => {
              goToSlideRef.current = props.goToSlide;
              return null;
            }}
            renderCenterRightControls={() => null}
            renderBottomCenterControls={() => null}
          >
            {slides.map((group) => (
              <Box key={group[0].id}>{renderGroup(group)}</Box>
            ))}
          </Carousel>

          {/* Paging dots */}
          <Flex justify='center' align='center' gap={2} mt={5} dir='rtl'>
            {slides.map((group, index) => (
              <Box
                as='button'
                type='button'
                key={group[0].id}
                aria-label={`عرض المجموعة ${index + 1}`}
                aria-current={currentSlide === index}
                onClick={() => goToSlideRef.current?.(index)}
                h='8px'
                w={currentSlide === index ? '24px' : '8px'}
                borderRadius='full'
                bg={currentSlide === index ? 'green.G500' : 'dark.D200'}
                transition='all 0.2s ease'
              />
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
};
